import type { DocNode, DocumentRunCreate, ReportDocument } from '@/lib/report-document'
import type { ReportRow } from '@/services/cube/report'

import type { DocumentDataSource, NamedResult } from '../nodes/data-source'
import {
  changeValueText,
  evaluateCondition,
  parseRef,
  refQueryName,
  valueText,
  type ConditionAttrs,
} from '../nodes/resolve'

/*
 * Dönem kopyasının açılması (materialize) ve "Verileri yenile" karşılaştırması
 * (docs/REPORT-DOCUMENTS-PLAN.md §4.2). Saf fonksiyonlar: sorgu sonuçları ve bağlam dışarıdan gelir,
 * ekran fazları (D7.2+) yalnız çağırır. Değer/koşul çözümü `nodes/resolve.ts`'indir.
 */

const DEFAULT_REPEAT_LIMIT = 50

export type QueryResults = Record<string, NamedResult | undefined>
export type ConditionResults = DocumentRunCreate['condition_results']
export type MaterializeContext = Pick<DocumentDataSource, 'periodLabel' | 'filterText'>
type Queries = ReportDocument['queries']

export interface Materialized {
  content: DocumentRunCreate['content']
  /**
   * `condition.<n>`: üst düzey koşul `true` = then; `repeat` içindeki koşul `then` seçilen satır
   * anahtarları. `repeat.<n>`: açılan satır anahtarları. `n` şablondaki sıradır (dal seçiminden
   * bağımsız), anahtar `data` ile birlikte `document_runs.condition_results`'a yazılır.
   */
  conditionResults: ConditionResults
}

export interface RefreshDiff {
  /** Sonucu değişen koşul anahtarları (`condition.<n>`). */
  conditions: string[]
  /** Satırları değişen tekrar anahtarları (`repeat.<n>`). */
  repeats: string[]
  /** Metni değişen `value` / `change` düğümü sayısı. */
  figures: number
}

interface Scope {
  /** `repeat`'in `over` sorgusu ve o anki satır; tekrar dışında tanımsız. */
  over?: string
  row?: ReportRow
  rowKey?: string
}

interface Ctx {
  queries: Queries
  results: QueryResults
  source: MaterializeContext
  keys: Map<DocNode, string>
  conditionResults: Record<string, boolean | string[]>
}

/** Şablondaki `condition` / `repeat` düğümlerine derinlik öncelikli sıra anahtarı verir. */
function numberBlocks(
  nodes: DocNode[],
  keys = new Map<DocNode, string>(),
  counts = { c: 0, r: 0 },
) {
  for (const node of nodes) {
    if (node.type === 'condition') keys.set(node, `condition.${counts.c++}`)
    if (node.type === 'repeat') keys.set(node, `repeat.${counts.r++}`)
    numberBlocks(node.content ?? [], keys, counts)
  }
  return keys
}

/** Satırın kararlı kimliği: sorgunun boyutları; boyutsuzsa sıra. */
function rowKeyOf(query: Queries[string] | undefined, view: string, row: ReportRow, index: number) {
  const dimensions = query?.dimensions ?? []
  if (dimensions.length === 0) return String(index)
  return dimensions.map((name) => String(row[`${view}.${name}`] ?? '')).join('|')
}

/** `row.` başvurusu tekrar satırına daralır; önceki dönem yalnız aynı anahtarlı satırdan gelir. */
function scopedResult(ref: string, ctx: Ctx, scope: Scope): NamedResult | undefined {
  const name = refQueryName(ref, scope.over)
  const result = name === undefined ? undefined : ctx.results[name]
  if (!result || parseRef(ref).head !== 'row' || !scope.row) return result
  const query = ctx.queries[name as string]
  const previous = result.previousRows?.find(
    (row, index) => rowKeyOf(query, result.view, row, index) === scope.rowKey,
  )
  return { ...result, rows: [scope.row], previousRows: previous ? [previous] : undefined }
}

function refText(node: DocNode, ctx: Ctx, scope: Scope): string {
  const ref = node.attrs?.of as string
  const result = scopedResult(ref, ctx, scope)
  return node.type === 'change' ? changeValueText(ref, result) : valueText(ref, ctx.source, result)
}

const isRowRef = (node: DocNode) => parseRef(node.attrs?.of as string).head === 'row'
const isContextRef = (node: DocNode) =>
  ['period', 'filters'].includes(parseRef(node.attrs?.of as string).head)

/** Bağlam (`period`/`filters`) ve satır (`row.`) başvuruları metne iner; sorgu başvuruları düğüm kalır. */
function expandInline(node: DocNode, ctx: Ctx, scope: Scope): DocNode[] {
  if (node.type !== 'value' && node.type !== 'change') return [node]
  if (!isContextRef(node) && !isRowRef(node)) return [node]
  const text = refText(node, ctx, scope)
  return text === '' ? [] : [{ type: 'text', text }]
}

/** Bitişik, işaretsiz metin düğümlerini birleştirir (çözülen değer çevresindeki metinle tek parça olur). */
function mergeText(nodes: DocNode[]): DocNode[] {
  const merged: DocNode[] = []
  for (const node of nodes) {
    const last = merged[merged.length - 1]
    if (last?.type === 'text' && node.type === 'text' && !last.marks && !node.marks) {
      merged[merged.length - 1] = { type: 'text', text: (last.text ?? '') + (node.text ?? '') }
    } else merged.push(node)
  }
  return merged
}

function pickBranch(node: DocNode, ctx: Ctx, scope: Scope): DocNode[] {
  const attrs = node.attrs as unknown as ConditionAttrs
  const right = attrs.right
  const left = scopedResult(attrs.left, ctx, scope)
  const rightResult = typeof right === 'string' ? scopedResult(right, ctx, scope) : undefined
  const then = evaluateCondition(attrs, left, rightResult)

  const key = ctx.keys.get(node) as string
  if (scope.rowKey === undefined) ctx.conditionResults[key] = then
  else if (then) {
    const chosen = ctx.conditionResults[key]
    ctx.conditionResults[key] = [...(Array.isArray(chosen) ? chosen : []), scope.rowKey]
  } else if (!Array.isArray(ctx.conditionResults[key])) ctx.conditionResults[key] = []

  const branch = node.content?.find(
    (child) => child.type === (then ? 'conditionThen' : 'conditionElse'),
  )
  return expandNodes(branch?.content ?? [], ctx, scope)
}

function expandRepeat(node: DocNode, ctx: Ctx): DocNode[] {
  const over = node.attrs?.over as string
  const limit = (node.attrs?.limit as number | undefined) ?? DEFAULT_REPEAT_LIMIT
  const result = ctx.results[over]
  const rows = (result?.rows ?? []).slice(0, limit)
  const query = ctx.queries[over]
  const view = result?.view ?? query?.view ?? ''
  const keys = rows.map((row, index) => rowKeyOf(query, view, row, index))
  ctx.conditionResults[ctx.keys.get(node) as string] = keys
  return rows.flatMap((row, index) =>
    expandNodes(node.content ?? [], ctx, { over, row, rowKey: keys[index] }),
  )
}

function expandNodes(nodes: DocNode[], ctx: Ctx, scope: Scope): DocNode[] {
  const expanded = nodes.flatMap((node): DocNode[] => {
    if (node.type === 'condition') return pickBranch(node, ctx, scope)
    if (node.type === 'repeat') return expandRepeat(node, ctx)
    if (node.type === 'value' || node.type === 'change') return expandInline(node, ctx, scope)
    if (!node.content) return [node]
    return [{ ...node, content: expandNodes(node.content, ctx, scope) }]
  })
  return mergeText(expanded)
}

function createCtx(
  definition: ReportDocument,
  results: QueryResults,
  source: MaterializeContext,
): Ctx {
  return {
    queries: definition.queries,
    results,
    source,
    keys: numberBlocks(definition.content.content),
    conditionResults: {},
  }
}

/** Şablonu sorgu sonuçlarıyla açar: koşul -> seçilen dal, tekrar -> satır başına bölüm, bağlam -> metin. */
export function materialize(
  definition: ReportDocument,
  results: QueryResults,
  source: MaterializeContext,
): Materialized {
  const ctx = createCtx(definition, results, source)
  const blocks = expandNodes(definition.content.content, ctx, {})
  return {
    content: { type: 'doc', content: blocks.length > 0 ? blocks : [{ type: 'paragraph' }] },
    conditionResults: ctx.conditionResults,
  }
}

/** Kopyadaki `value` / `change` düğümlerinin (satır/bağlam dışı) o sonuçlarla gösterdiği metinler. */
function figureTexts(nodes: DocNode[], ctx: Ctx, texts: string[] = []): string[] {
  for (const node of nodes) {
    if (node.type === 'value' || node.type === 'change') texts.push(refText(node, ctx, {}))
    figureTexts(node.content ?? [], ctx, texts)
  }
  return texts
}

const sameValue = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

/**
 * "Verileri yenile" farkı. Metin (`content`) değişmez; yalnız işaretlenecekler bulunur:
 * sonucu değişen koşullar, satırları değişen tekrarlar, metni değişen rakamlar.
 * `previousConditionResults`: üretimde saklananlar; yenisi şablon `next` sonuçlarıyla yeniden hesaplanır.
 */
export function diffRefresh(args: {
  definition: ReportDocument
  /** Kopyanın (kullanıcı düzenlemeli) açılmış içeriği. */
  content: DocumentRunCreate['content']
  previousConditionResults: ConditionResults
  previous: QueryResults
  next: QueryResults
  source: MaterializeContext
}): RefreshDiff {
  const { definition, content, previousConditionResults, previous, next, source } = args
  const nextResults = materialize(definition, next, source).conditionResults
  const changed = (prefix: string) =>
    [...new Set([...Object.keys(previousConditionResults), ...Object.keys(nextResults)])]
      .filter((key) => key.startsWith(`${prefix}.`))
      .filter((key) => !sameValue(previousConditionResults[key], nextResults[key]))

  const before = figureTexts(content.content, createCtx(definition, previous, source))
  const after = figureTexts(content.content, createCtx(definition, next, source))
  return {
    conditions: changed('condition'),
    repeats: changed('repeat'),
    figures: before.filter((text, index) => text !== after[index]).length,
  }
}
